import React, { useState, useEffect, useCallback } from 'react';
import { testimonials } from '../data/mockData';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating]);

  useEffect(() => {
    const interval = setInterval(() => handleNext(), 5000);
    return () => clearInterval(interval);
  }, [handleNext]);

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 400);
  };

  const getVisible = () => {
    const prev = (currentIndex - 1 + testimonials.length) % testimonials.length;
    const next = (currentIndex + 1) % testimonials.length;
    return [testimonials[prev], testimonials[currentIndex], testimonials[next]];
  };

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-28 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#06B6D4]/[0.04] rounded-full blur-[80px]" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div className="max-w-2xl mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3B82F6]/10 border border-[#3B82F6]/20 mb-5">
            <span className="text-xs font-semibold text-[#3B82F6] uppercase tracking-wider">Client Success</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-5">
            What Our <span className="text-gradient">Clients Say</span>
          </h2>
          <p className="text-[#A1A1AA] leading-relaxed">Real results from clients who protected their brands with us.</p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-4 mb-8">
          {getVisible().map((t, i) => (
            <div key={`${t.id}-${i}`}
              className={`glass-card rounded-2xl p-7 transition-all duration-300 cursor-default ${i === 1 ? 'border-[#3B82F6]/30 shadow-lg shadow-[#3B82F6]/5 scale-[1.02]' : 'opacity-80 hover:opacity-100'}`}>
              <Quote className="w-8 h-8 text-[#3B82F6]/20 mb-4" />
              <div className="flex gap-0.5 mb-4">
                {[...Array(t.rating)].map((_, s) => (
                  <Star key={s} className="w-4 h-4 fill-[#3B82F6] text-[#3B82F6]" />
                ))}
              </div>
              <p className="text-white/80 leading-relaxed mb-6 min-h-[100px] text-sm">{t.content}</p>
              <div className="flex items-center gap-3 pt-4 border-t border-white/[0.06]">
                <div className="w-10 h-10 rounded-xl btn-gradient flex items-center justify-center text-white font-semibold text-sm">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-sm text-white">{t.name}</div>
                  <div className="text-xs text-[#71717A]">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Button data-testid="testimonial-prev" onClick={handlePrev} disabled={isAnimating}
            className="w-10 h-10 p-0 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-white border border-white/[0.08]">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-1.5">
            {testimonials.map((_, index) => (
              <button key={index}
                onClick={() => { if (!isAnimating) { setIsAnimating(true); setCurrentIndex(index); setTimeout(() => setIsAnimating(false), 400); } }}
                className={`rounded-full transition-all duration-300 ${index === currentIndex ? 'w-6 h-1.5 btn-gradient' : 'w-1.5 h-1.5 bg-white/[0.15] hover:bg-white/[0.3]'}`} />
            ))}
          </div>
          <Button data-testid="testimonial-next" onClick={handleNext} disabled={isAnimating}
            className="w-10 h-10 p-0 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-white border border-white/[0.08]">
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
